import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import {
    CreditCard, Calendar, DollarSign, Activity,
    CheckCircle, XCircle, Clock, ChevronDown, ChevronUp,
    RefreshCw, Plus, Trash2, Loader2, Star, History,
    X, MapPin, Mail, User, Download
} from 'lucide-react';

export default function Billing() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    
    const [billingData, setBillingData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Period type: 'last7days', 'monthly', 'yearly', 'custom'
    const [periodType, setPeriodType] = useState('monthly');
    
    // Selected month for monthly view (YYYY-MM format)
    const [selectedMonth, setSelectedMonth] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    
    // Selected year for yearly view
    const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
    
    // Custom date range
    const [customStartDate, setCustomStartDate] = useState(() => {
        const now = new Date();
        now.setDate(now.getDate() - 30);
        return now.toISOString().split('T')[0];
    });
    const [customEndDate, setCustomEndDate] = useState(() => {
        return new Date().toISOString().split('T')[0];
    });
    
    // Generate last 12 months for dropdown
    const monthOptions = (() => {
        const months = [];
        const now = new Date();
        for (let i = 0; i < 12; i++) {
            const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
            months.push({ value, label });
        }
        return months;
    })();
    
    // Generate last 3 years for yearly dropdown
    const yearOptions = (() => {
        const years = [];
        const currentYear = new Date().getFullYear();
        for (let i = 0; i < 3; i++) {
            years.push(currentYear - i);
        }
        return years;
    })();
    
    // Compute the period string to send to API
    const selectedPeriod = useMemo(() => {
        if (periodType === 'last7days') return 'last7days';
        if (periodType === 'yearly') return `year-${selectedYear}`;
        if (periodType === 'custom') return `custom-${customStartDate}-${customEndDate}`;
        return selectedMonth; // monthly - YYYY-MM format
    }, [periodType, selectedMonth, selectedYear, customStartDate, customEndDate]);
    
    // Payment methods state
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [defaultPaymentMethod, setDefaultPaymentMethod] = useState(null);
    const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
    const [addingCard, setAddingCard] = useState(false);
    const [cardAddedSuccess, setCardAddedSuccess] = useState(false);
    
    // Payment history state
    const [paymentHistory, setPaymentHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [showHistory, setShowHistory] = useState(true);
    
    // Download invoice state
    const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
    
    // Card details modal state
    const [selectedCard, setSelectedCard] = useState(null);

    useEffect(() => {
        const setup = searchParams.get('setup');
        if (setup === 'success') {
            setCardAddedSuccess(true);
            navigate('/dashboard/billing', { replace: true });
        } else if (setup === 'cancelled') {
            navigate('/dashboard/billing', { replace: true });
        }
    }, [searchParams, navigate]);

    const fetchBillingData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.get(`/api/billing/usage?period=${selectedPeriod}`);
            setBillingData(response.data);
        } catch (err) {
            console.error('Failed to fetch billing data:', err);
            setError('Failed to load billing data. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [selectedPeriod]);

    const fetchPaymentMethods = useCallback(async () => {
        setLoadingPaymentMethods(true);
        try {
            const response = await api.get('/api/billing/payment-methods');
            setPaymentMethods(response.data.payment_methods || []);
            setDefaultPaymentMethod(response.data.default_payment_method);
        } catch (err) {
            console.error('Failed to fetch payment methods:', err);
        } finally {
            setLoadingPaymentMethods(false);
        }
    }, []);

    const fetchPaymentHistory = useCallback(async () => {
        setLoadingHistory(true);
        try {
            const response = await api.get('/api/billing/history');
            setPaymentHistory(response.data.payments || []);
        } catch (err) {
            console.error('Failed to fetch payment history:', err);
        } finally {
            setLoadingHistory(false);
        }
    }, []);

    useEffect(() => {
        fetchBillingData();
        fetchPaymentMethods();
        fetchPaymentHistory();
    }, []);

    // Auto-fetch when filter changes (except for custom - requires Apply button)
    useEffect(() => {
        if (periodType !== 'custom') {
            fetchBillingData();
        }
    }, [periodType, selectedMonth, selectedYear]);

    const handleApplyFilters = () => {
        fetchBillingData();
    };

    const handleAddCard = async () => {
        setAddingCard(true);
        try {
            const response = await api.post('/api/billing/create-setup-session');
            if (response.data.url) {
                window.location.href = response.data.url;
            } else {
                throw new Error('No redirect URL received');
            }
        } catch (err) {
            console.error('Failed to add card:', err);
            alert('Failed to add a payment method. Please try again.');
            setAddingCard(false);
        }
    };

    const handleSetDefaultCard = async (paymentMethodId) => {
        try {
            await api.post('/api/billing/payment-methods/default', {
                payment_method_id: paymentMethodId
            });
            setDefaultPaymentMethod(paymentMethodId);
            fetchPaymentMethods();
        } catch (err) {
            console.error('Failed to set default card:', err);
            alert('Failed to set the default payment method.');
        }
    };

    const handleDeleteCard = async (paymentMethodId) => {
        // Prevent deleting the last card
        if (paymentMethods.length <= 1) {
            alert('You must have at least one payment method. Please add a new card before removing this one.');
            return;
        }
        if (!window.confirm('Are you sure you want to remove this payment method?')) {
            return;
        }
        try {
            await api.delete(`/api/billing/payment-methods/${paymentMethodId}`);
            fetchPaymentMethods();
        } catch (err) {
            console.error('Failed to delete card:', err);
            alert('Failed to remove the payment method.');
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getCardBrandIcon = (brand) => {
        const icons = {
            visa: '💳',
            mastercard: '💳',
            amex: '💳',
            discover: '💳',
        };
        return icons[brand?.toLowerCase()] || '💳';
    };

    const getCardBrandName = (brand) => {
        const names = {
            visa: 'Visa',
            mastercard: 'Mastercard',
            amex: 'American Express',
            discover: 'Discover',
        };
        return names[brand?.toLowerCase()] || brand || 'Card';
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center h-64 space-y-4">
                <XCircle className="w-12 h-12 text-red-500" />
                <p className="text-gray-600">{error}</p>
                <button
                    onClick={fetchBillingData}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                    <RefreshCw className="w-4 h-4" />
                    Retry
                </button>
            </div>
        );
    }

    const { billing_period, usage_summary, pricing } = billingData || {};
    const amountDue = usage_summary?.total_cost || 0;

    return (
        <div className="space-y-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Billing</h1>
                        <p className="text-gray-500 text-sm mt-1">Manage your usage and payments</p>
                    </div>
                </div>
                
                {/* Filters Row */}
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                    <div className="flex flex-wrap items-center gap-3">
                        {/* Quick Period Buttons */}
                        <div className="flex items-center rounded-lg border border-gray-200 p-1 bg-gray-50">
                            <button
                                onClick={() => setPeriodType('last7days')}
                                className={`px-3 py-1.5 text-sm font-medium rounded-md transition ${
                                    periodType === 'last7days'
                                        ? 'bg-white text-blue-600 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                Last 7 days
                            </button>
                            <button
                                onClick={() => setPeriodType('monthly')}
                                className={`px-3 py-1.5 text-sm font-medium rounded-md transition ${
                                    periodType === 'monthly'
                                        ? 'bg-white text-blue-600 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                Monthly
                            </button>
                            <button
                                onClick={() => setPeriodType('yearly')}
                                className={`px-3 py-1.5 text-sm font-medium rounded-md transition ${
                                    periodType === 'yearly'
                                        ? 'bg-white text-blue-600 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                Yearly
                            </button>
                            <button
                                onClick={() => setPeriodType('custom')}
                                className={`px-3 py-1.5 text-sm font-medium rounded-md transition ${
                                    periodType === 'custom'
                                        ? 'bg-white text-blue-600 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                Custom
                            </button>
                        </div>
                        
                        {/* Divider */}
                        <div className="h-8 w-px bg-gray-200" />
                        
                        {/* Month Selector */}
                        {periodType === 'monthly' && (
                            <select
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                {monthOptions.map(month => (
                                    <option key={month.value} value={month.value}>{month.label}</option>
                                ))}
                            </select>
                        )}
                        
                        {/* Year Selector */}
                        {periodType === 'yearly' && (
                            <select
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                {yearOptions.map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        )}
                        
                        {/* Custom Date Range */}
                        {periodType === 'custom' && (
                            <div className="flex items-center gap-2">
                                <div className="flex items-center gap-2 text-sm">
                                    <span className="text-gray-500">From</span>
                                    <input
                                        type="date"
                                        value={customStartDate}
                                        onChange={(e) => setCustomStartDate(e.target.value)}
                                        className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                    <span className="text-gray-500">To</span>
                                    <input
                                        type="date"
                                        value={customEndDate}
                                        onChange={(e) => setCustomEndDate(e.target.value)}
                                        className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>
                        )}
                        
                        {/* Apply Button - only for custom filter */}
                        {periodType === 'custom' && (
                            <button
                                onClick={handleApplyFilters}
                                disabled={loading}
                                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                            >
                                {loading ? 'Loading...' : 'Apply'}
                            </button>
                        )}
                        
                        {/* Refresh Button */}
                        <button
                            onClick={fetchBillingData}
                            className="ml-auto p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition"
                            title="Refresh"
                        >
                            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Alerts */}
            {cardAddedSuccess && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <p className="text-green-800 text-sm flex-1">Payment method added successfully!</p>
                    <button onClick={() => setCardAddedSuccess(false)} className="text-green-600 hover:text-green-800">
                        <XCircle className="w-5 h-5" />
                    </button>
                </div>
            )}

            {/* Balance Card */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-8">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                        <div>
                            <div className="flex items-center gap-2 text-white/100 mb-2">
                                <Calendar className="w-4 h-4" />
                                <span className="text-sm">
                                    {billing_period ? `${formatDate(billing_period.start)} — ${formatDate(billing_period.end)}` : 'Current Period'}
                                </span>
                            </div>
                            <div className="flex items-baseline gap-3">
                                <span className="text-5xl font-bold text-white">{formatCurrency(amountDue)}</span>
                                {amountDue > 0 ? (
                                    <span className="text-white font-medium">Balance due</span>
                                ) : (
                                    <span className="text-green-300 font-medium">all paid</span>
                                )}
                            </div>
                            <p className="text-white/100 text-sm mt-2">
                                {usage_summary?.total_credits || 0} credits × {formatCurrency(pricing?.credit_price || 2)} per credit
                            </p>
                        </div>
                    </div>
                </div>
                
                {/* Quick Stats */}
                <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-gray-100">
                    <div className="px-6 py-5">
                        <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                            <Activity className="w-4 h-4" />
                            Total Calls
                        </div>
                        <p className="text-2xl font-semibold text-gray-900">
                            {usage_summary?.total_calls?.toLocaleString() || 0}
                        </p>
                    </div>
                    <div className="px-6 py-5">
                        <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            Success Rate
                        </div>
                        <p className="text-2xl font-semibold text-gray-900">
                            {usage_summary?.success_rate?.toFixed(1) || 0}%
                        </p>
                    </div>
                    <div className="px-6 py-5">
                        <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                            <DollarSign className="w-4 h-4 text-blue-500" />
                            Credits Used
                        </div>
                        <p className="text-2xl font-semibold text-gray-900">
                            {usage_summary?.total_credits?.toLocaleString() || 0}
                        </p>
                    </div>
                    <div className="px-6 py-5">
                        <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                            <Clock className="w-4 h-4 text-orange-500" />
                            Days Left
                        </div>
                        <p className="text-2xl font-semibold text-gray-900">
                            {billing_period?.days_remaining || 0}
                        </p>
                    </div>
                </div>
            </div>

            {/* Payment Methods & History */}
            <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
                {/* Payment Methods - Takes more space */}
                <div className="xl:col-span-3 bg-white rounded-xl border border-gray-200">
                    <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-50 rounded-lg">
                                <CreditCard className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <h2 className="font-semibold text-gray-900">Payment Methods</h2>
                                <p className="text-sm text-gray-500">Manage your cards for billing</p>
                            </div>
                        </div>
                        {loadingPaymentMethods && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                    </div>
                    
                    <div className="p-6">
                        {paymentMethods.length > 0 ? (
                            <div className="space-y-3">
                                {paymentMethods.map((pm) => (
                                    <div 
                                        key={pm.id} 
                                        onClick={() => setSelectedCard(pm)}
                                        className={`flex items-center justify-between p-4 rounded-xl border-2 transition cursor-pointer ${
                                            pm.is_default 
                                                ? 'border-blue-500 bg-blue-50/50 hover:bg-blue-100/50' 
                                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                        }`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className={`p-3 rounded-lg ${pm.is_default ? 'bg-blue-100' : 'bg-gray-100'}`}>
                                                <span className="text-2xl">{getCardBrandIcon(pm.brand)}</span>
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium text-gray-900">
                                                        {getCardBrandName(pm.brand)} •••• {pm.last4}
                                                    </span>
                                                    {pm.is_default && (
                                                        <span className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full font-medium">
                                                            Default
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-sm text-gray-500 mt-0.5">
                                                    Expires {pm.exp_month}/{pm.exp_year}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                            {!pm.is_default && (
                                                <button
                                                    onClick={() => handleSetDefaultCard(pm.id)}
                                                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                                                    title="Set as default"
                                                >
                                                    <Star className="w-5 h-5" />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => handleDeleteCard(pm.id)}
                                                disabled={paymentMethods.length <= 1}
                                                className={`p-2 rounded-lg transition ${
                                                    paymentMethods.length <= 1 
                                                        ? 'text-gray-300 cursor-not-allowed' 
                                                        : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                                                }`}
                                                title={paymentMethods.length <= 1 ? 'Add another card first' : 'Remove card'}
                                            >
                                                <Trash2 className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                
                                <button
                                    onClick={handleAddCard}
                                    disabled={addingCard}
                                    className="w-full mt-4 px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50/50 transition flex items-center justify-center gap-2 font-medium"
                                >
                                    {addingCard ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                        <Plus className="w-5 h-5" />
                                    )}
                                    Add New Card
                                </button>
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <div className="p-4 bg-gray-100 rounded-full w-fit mx-auto mb-4">
                                    <CreditCard className="w-10 h-10 text-gray-400" />
                                </div>
                                <h3 className="font-medium text-gray-900 mb-1">No payment method</h3>
                                <p className="text-gray-500 text-sm mb-6 max-w-sm mx-auto">
                                    Add a credit or debit card to pay for your API usage
                                </p>
                                <button
                                    onClick={handleAddCard}
                                    disabled={addingCard}
                                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium inline-flex items-center gap-2"
                                >
                                    {addingCard ? (
                                        <>
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                            Setting up...
                                        </>
                                    ) : (
                                        <>
                                            <Plus className="w-5 h-5" />
                                            Add Payment Method
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Payment History - Compact */}
                <div className="xl:col-span-2 bg-white rounded-xl border border-gray-200">
                    <button
                        onClick={() => setShowHistory(!showHistory)}
                        className="w-full px-6 py-4 border-b border-gray-100 flex items-center justify-between hover:bg-gray-50 transition"
                    >
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-gray-100 rounded-lg">
                                <History className="w-5 h-5 text-gray-600" />
                            </div>
                            <div className="text-left">
                                <h2 className="font-semibold text-gray-900">Payment History</h2>
                                <p className="text-sm text-gray-500">{paymentHistory.length} transactions</p>
                            </div>
                        </div>
                        {showHistory ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                    </button>
                    
                    {showHistory && (
                        <div className="max-h-96 overflow-y-auto">
                            {loadingHistory ? (
                                <div className="p-8 text-center">
                                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                                </div>
                            ) : paymentHistory.length > 0 ? (
                                <div className="divide-y divide-gray-100">
                                    {paymentHistory.slice(0, 5).map((payment) => (
                                        <div key={payment.id} className="px-6 py-4 hover:bg-gray-50 transition">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="font-medium text-gray-900">
                                                        {formatCurrency(payment.amount)}
                                                    </p>
                                                    <p className="text-sm text-gray-500">
                                                        {formatDate(payment.paid_at || payment.created_at)}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {payment.status === 'paid' && (
                                                        <button
                                                            onClick={async () => {
                                                                setDownloadingInvoiceId(payment.id);
                                                                try {
                                                                    const timezoneOffset = new Date().getTimezoneOffset();
                                                                    const response = await api.get(`/api/billing/invoice/${payment.id}?timezone_offset=${timezoneOffset}`);
                                                                    if (response.data.invoice_url) {
                                                                        // Fetch the PDF and download it
                                                                        const pdfResponse = await fetch(response.data.invoice_url);
                                                                        const blob = await pdfResponse.blob();
                                                                        const url = window.URL.createObjectURL(blob);
                                                                        const link = document.createElement('a');
                                                                        link.href = url;
                                                                        link.download = `invoice_${response.data.invoice_number || payment.id}.pdf`;
                                                                        document.body.appendChild(link);
                                                                        link.click();
                                                                        document.body.removeChild(link);
                                                                        window.URL.revokeObjectURL(url);
                                                                    }
                                                                } catch (err) {
                                                                    console.error('Failed to download invoice:', err);
                                                                    alert('Failed to download the invoice. Please try again.');
                                                                } finally {
                                                                    setDownloadingInvoiceId(null);
                                                                }
                                                            }}
                                                            disabled={downloadingInvoiceId === payment.id}
                                                            className={`p-1.5 rounded-lg transition flex items-center gap-1.5 ${
                                                                downloadingInvoiceId === payment.id 
                                                                    ? 'bg-blue-50 text-blue-500 cursor-wait' 
                                                                    : 'text-blue-600 hover:bg-blue-50'
                                                            }`}
                                                            title="Download Invoice"
                                                        >
                                                            {downloadingInvoiceId === payment.id ? (
                                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                            ) : (
                                                                <Download className="w-4 h-4" />
                                                            )}
                                                        </button>
                                                    )}
                                                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                                                        payment.status === 'paid' 
                                                            ? 'bg-green-100 text-green-700' 
                                                            : payment.status === 'failed'
                                                            ? 'bg-red-100 text-red-700'
                                                            : 'bg-yellow-100 text-yellow-700'
                                                    }`}>
                                                        {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                                                    </span>
                                                </div>
                                            </div>
                                            {payment.payment_method_brand && (
                                                <p className="text-xs text-gray-400 mt-1">
                                                    {payment.payment_method_brand} •••• {payment.payment_method_last4}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="p-8 text-center">
                                    <History className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                                    <p className="text-gray-500 text-sm">No payment history yet</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Card Details Modal */}
            {selectedCard && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setSelectedCard(null)}>
                    <div 
                        className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-5 py-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <CreditCard className="w-5 h-5 text-white" />
                                    <h3 className="font-semibold text-white">Card Details</h3>
                                    <span className="text-blue-200 text-sm">• {getCardBrandName(selectedCard.brand)}</span>
                                </div>
                                <button
                                    onClick={() => setSelectedCard(null)}
                                    className="p-1.5 hover:bg-white/20 rounded-lg transition"
                                >
                                    <X className="w-4 h-4 text-white" />
                                </button>
                            </div>
                        </div>

                        {/* Card Visual */}
                        <div className="px-6 py-4">
                            <div className={`p-5 rounded-xl ${selectedCard.is_default ? 'bg-gradient-to-br from-blue-500 to-blue-700' : 'bg-gradient-to-br from-gray-700 to-gray-900'}`}>
                                <div className="flex justify-between items-start mb-8">
                                    <span className="text-2xl">{getCardBrandIcon(selectedCard.brand)}</span>
                                    {selectedCard.is_default && (
                                        <span className="px-2 py-1 bg-white/20 text-white text-xs rounded-full">Default</span>
                                    )}
                                </div>
                                <div className="text-white font-mono text-lg tracking-widest mb-4">
                                    •••• •••• •••• {selectedCard.last4}
                                </div>
                                <div className="flex justify-between items-end">
                                    <div>
                                        <p className="text-white/60 text-xs uppercase">Card Holder</p>
                                        <p className="text-white font-medium">
                                            {selectedCard.billing_details?.name || 'Card Holder'}
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-white/60 text-xs uppercase">Expires</p>
                                        <p className="text-white font-medium">
                                            {String(selectedCard.exp_month).padStart(2, '0')}/{String(selectedCard.exp_year).slice(-2)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Card Information */}
                        <div className="px-6 pb-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-3 bg-gray-50 rounded-lg">
                                    <p className="text-xs text-gray-500 uppercase mb-1">Brand</p>
                                    <p className="font-medium text-gray-900">{getCardBrandName(selectedCard.brand)}</p>
                                </div>
                                <div className="p-3 bg-gray-50 rounded-lg">
                                    <p className="text-xs text-gray-500 uppercase mb-1">Last 4 Digits</p>
                                    <p className="font-medium text-gray-900">{selectedCard.last4}</p>
                                </div>
                            </div>

                            {/* Billing Address */}
                            {selectedCard.billing_details?.address && (
                                <div className="p-4 bg-gray-50 rounded-lg">
                                    <div className="flex items-center gap-2 mb-3">
                                        <MapPin className="w-4 h-4 text-gray-500" />
                                        <p className="text-sm font-medium text-gray-700">Billing Address</p>
                                    </div>
                                    <div className="text-sm text-gray-600 space-y-1">
                                        {selectedCard.billing_details.name && (
                                            <div className="flex items-center gap-2">
                                                <User className="w-3 h-3 text-gray-400" />
                                                <span>{selectedCard.billing_details.name}</span>
                                            </div>
                                        )}
                                        {selectedCard.billing_details.email && (
                                            <div className="flex items-center gap-2">
                                                <Mail className="w-3 h-3 text-gray-400" />
                                                <span>{selectedCard.billing_details.email}</span>
                                            </div>
                                        )}
                                        {selectedCard.billing_details.address.line1 && (
                                            <p>{selectedCard.billing_details.address.line1}</p>
                                        )}
                                        {selectedCard.billing_details.address.line2 && (
                                            <p>{selectedCard.billing_details.address.line2}</p>
                                        )}
                                        <p>
                                            {[
                                                selectedCard.billing_details.address.city,
                                                selectedCard.billing_details.address.state,
                                                selectedCard.billing_details.address.postal_code
                                            ].filter(Boolean).join(', ')}
                                        </p>
                                        {selectedCard.billing_details.address.country && (
                                            <p>{selectedCard.billing_details.address.country}</p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Actions */}
                            <div className="flex gap-3 pt-2">
                                {!selectedCard.is_default && (
                                    <button
                                        onClick={() => {
                                            handleSetDefaultCard(selectedCard.id);
                                            setSelectedCard(null);
                                        }}
                                        className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium flex items-center justify-center gap-2"
                                    >
                                        <Star className="w-4 h-4" />
                                        Set as Default
                                    </button>
                                )}
                                <button
                                    onClick={() => {
                                        if (paymentMethods.length <= 1) {
                                            alert('You must have at least one payment method. Please add a new card before removing this one.');
                                            return;
                                        }
                                        handleDeleteCard(selectedCard.id);
                                        setSelectedCard(null);
                                    }}
                                    disabled={paymentMethods.length <= 1}
                                    className={`${selectedCard.is_default ? 'flex-1' : ''} px-4 py-2.5 border rounded-lg transition font-medium flex items-center justify-center gap-2 ${
                                        paymentMethods.length <= 1 
                                            ? 'border-gray-200 text-gray-400 cursor-not-allowed' 
                                            : 'border-red-200 text-red-600 hover:bg-red-50'
                                    }`}
                                    title={paymentMethods.length <= 1 ? 'Add another card first to remove this one' : 'Remove card'}
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Remove Card
                                </button>
                            </div>
                            {paymentMethods.length <= 1 && (
                                <p className="text-xs text-gray-500 text-center mt-2">
                                    At least one payment method is required
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
